/**
 * Payout destination validation (plan-005 OP-11, design §18.8.1, contract IC-53).
 *
 * The customer api validates a destination with {@link validatePayoutDestination} BEFORE the IBAN is
 * written to the payout Key Vault; an invalid one is answered `422 payout_destination_invalid` naming
 * the field, and **the IBAN is never echoed** — not in the error, not in a log. The result names the
 * field only.
 *
 * Rules:
 *   - IBAN: upper-cased, spaces removed; the country must be a SEPA scheme country (the closed
 *     {@link SEPA_IBAN_LENGTH} table, which also fixes the length per country); ISO 13616 mod-97 = 1.
 *   - BIC: optional, required only for a SEPA country outside the EEA ({@link SEPA_NON_EEA_COUNTRY});
 *     when given it is 8 or 11 characters, `AAAA BB CC (DDD)`.
 *   - Beneficiary name: 1–140 characters after trimming.
 *   - Tax identifier: required. For a Greek destination it is the ΑΦΜ — 9 digits with its check digit;
 *     for any other country 1–32 characters.
 *
 * Pure and synchronous, with no platform dependency: `packages/domain` is imported by the browser
 * consoles as well as by Node, and the console validates as the owner types.
 */
/**
 * The SEPA scheme countries that issue their own IBANs, with each country's IBAN length (ISO 13616
 * registry). Territories that use another country's IBAN (Åland, the French overseas departments,
 * Jersey, Guernsey, the Isle of Man) are covered by that country's code. A country absent here is
 * refused, never guessed: a payout is a SEPA credit transfer.
 */
export const SEPA_IBAN_LENGTH = Object.freeze({
    AD: 24, AL: 28, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18,
    EE: 20, ES: 24, FI: 18, FR: 27, GB: 22, GI: 23, GR: 27, HR: 21, HU: 28, IE: 22,
    IS: 26, IT: 27, LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19,
    MT: 31, NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24, SM: 27,
    VA: 22,
});
/** SEPA countries outside the EEA: a transfer there needs the BIC. */
export const SEPA_NON_EEA_COUNTRY = Object.freeze([
    "AD", "AL", "CH", "GB", "GI", "MC", "MD", "ME", "MK", "SM", "VA",
]);
/** Upper-cases and removes every whitespace character. Validates nothing. */
export function normaliseIban(raw) {
    return raw.replace(/\s+/g, "").toUpperCase();
}
/** True when a NORMALISED IBAN is a SEPA IBAN of the right length whose mod-97 check holds. */
export function isValidSepaIban(iban) {
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(iban))
        return false;
    const expected = SEPA_IBAN_LENGTH[iban.slice(0, 2)];
    if (expected === undefined || iban.length !== expected)
        return false;
    return mod97(`${iban.slice(4)}${iban.slice(0, 4)}`) === 1;
}
/** ISO 7064 MOD 97-10 over the letters-as-numbers expansion, digit by digit (no big integers). */
function mod97(rearranged) {
    let remainder = 0;
    for (const character of rearranged) {
        const code = character.charCodeAt(0);
        const value = code >= 65 ? String(code - 55) : character;
        for (const digit of value) {
            remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
        }
    }
    return remainder;
}
/** A BIC: 4 letters (bank), 2 letters (country), 2 alphanumerics (location), optional 3 (branch). */
export function isValidBic(bic) {
    return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic);
}
/**
 * The Greek tax identifier (ΑΦΜ): 9 digits, the ninth the check digit —
 * `(Σ dᵢ·2^(8−i) for i = 0…7) mod 11 mod 10`. All zeros is refused.
 */
export function isValidGreekTaxIdentifier(value) {
    if (!/^[0-9]{9}$/.test(value) || value === "000000000")
        return false;
    let sum = 0;
    for (let index = 0; index < 8; index += 1) {
        sum += (value.charCodeAt(index) - 48) * 2 ** (8 - index);
    }
    return (sum % 11) % 10 === value.charCodeAt(8) - 48;
}
/** Every rule of design §18.8.1, in form order; the first failing field is named. */
export function validatePayoutDestination(input) {
    const iban = normaliseIban(input.iban);
    if (!isValidSepaIban(iban))
        return { ok: false, field: "iban" };
    const countryCode = iban.slice(0, 2);
    const bicRaw = input.bic === null ? "" : input.bic.replace(/\s+/g, "").toUpperCase();
    const bic = bicRaw === "" ? null : bicRaw;
    if (bic === null && SEPA_NON_EEA_COUNTRY.includes(countryCode))
        return { ok: false, field: "bic" };
    if (bic !== null && !isValidBic(bic))
        return { ok: false, field: "bic" };
    const beneficiaryName = input.beneficiaryName.trim();
    if (beneficiaryName.length < 1 || beneficiaryName.length > 140) {
        return { ok: false, field: "beneficiary_name" };
    }
    const taxIdentifier = input.taxIdentifier.trim();
    const taxValid = countryCode === "GR"
        ? isValidGreekTaxIdentifier(taxIdentifier)
        : taxIdentifier.length >= 1 && taxIdentifier.length <= 32;
    if (!taxValid)
        return { ok: false, field: "tax_identifier" };
    return {
        ok: true,
        destination: Object.freeze({
            iban,
            ibanLastFour: iban.slice(-4),
            countryCode,
            bic,
            beneficiaryName,
            taxIdentifier,
        }),
    };
}
//# sourceMappingURL=iban.js.map